import React, { useMemo, useRef } from 'react'
import Header from './Header'
import ParentHeader from './ParentHeader'
import { useStore } from '../../store/store';
import { getMaxDepth, buildHeaderRows, getLeafColumnsCount } from './tableHeaderUtils';
import { rowStyleCss } from '../../shared/tables/tableRowStyle';

const TableHead = ({
  table,
  columns,
  isSelected,
  boxId,
  handleClickInTable,
  updateTheadHeight,
  tableStyle,
  convertAlign,
  convertVerticalAlign,
  rotation,
  setColumnSizeVars,
  columnSizeVars,
  computeMaxColumnWidth,
  drawMode,
  box,
  controlRowsClicked,
  setControlRowsClicked,
  verticalAlign,
  onControlRowClick,
  firstRowStyle
}) => {
  const configuration = useStore(state => state.configuration);
  // Bordures fusionnées : le trait sous l'en-tête est le haut de la 1ʳᵉ ligne,
  // il en prend la couleur de bordure réglée (cf. rowStyleCss).
  const bodyEdgeStyle = rowStyleCss(null, firstRowStyle);
  const columnsDisposition = box.content?.columnsDisposition;
  const hasDisposition =
    columnsDisposition &&
    columnsDisposition.length > 0 &&
    getLeafColumnsCount(columnsDisposition) === columns.length;

  // Ref vers le premier tr pour que chaque Header puisse mesurer son offset
  const firstRowRef = useRef(null);

  // Un seul calcul des pourcentages pour toutes les colonnes (évite N useMemo dans chaque Header)
  const columnWidthPercentMap = useMemo(() => {
    const cols = box?.content?.columns
    if (!cols?.length) return {}
    const widths = cols.map(
      (c) => columnSizeVars?.[`--${c.key}-size`] ?? c.width ?? 0
    )
    const total = widths.reduce((sum, w) => sum + w, 0)
    if (total <= 0) return {}

    const minW = Math.min(...widths)
    const maxW = Math.max(...widths)
    // Largeur totale impaire → colonnes en pixels à ±1 px : éviter 49,93 / 50,07 à l’affichage
    const nearlyUniformColumns =
      minW > 0 &&
      maxW - minW <= 1 &&
      minW / maxW >= 0.995

    const map = {}
    if (nearlyUniformColumns) {
      const n = cols.length
      const base = Number((100 / n).toFixed(2))
      let acc = 0
      cols.forEach((c, i) => {
        if (i === n - 1) {
          map[c.key] = Number((100 - acc).toFixed(2))
        } else {
          map[c.key] = base
          acc += base
        }
      })
    } else {
      cols.forEach((c) => {
        const w = columnSizeVars?.[`--${c.key}-size`] ?? c.width
        map[c.key] =
          w != null ? Number(((w / total) * 100).toFixed(2)) : null
      })
    }
    return map
  }, [box?.content?.columns, columnSizeVars])

  // Génère la structure de headers hiérarchiques si columnsDisposition existe
  const headerRows = useMemo(() => {
    if (!hasDisposition) return null;
    const maxDepth = getMaxDepth(columnsDisposition);
    return buildHeaderRows(columnsDisposition, columns, maxDepth);
  }, [hasDisposition, columnsDisposition, columns]);

  const renderHeader = (headerData, globalColIdx, rowIndex) => {
    const { key, title, rowSpan, colSpan, isParent, columnData, leafIndex, isLastColumn } = headerData;

    // Si c'est une colonne parent, vérifier si elle existe dans columns
    if (isParent) {
      const colExistsInColumns = columns.find(c => c.key === key);

      // Si la colonne parent n'existe pas dans columns, rendre un ParentHeader
      if (!colExistsInColumns) {
        return (
          <ParentHeader
            key={key}
            rowSpan={rowSpan}
            colSpan={colSpan}
            columnKey={key}
            title={title}
            isLastColumn={isLastColumn}
            tableStyle={tableStyle}
            configuration={configuration}
            boxId={boxId}
            tableIsSelected={isSelected}
            updateTheadHeight={updateTheadHeight}
          />
        );
      }
    }

    // Colonne qui existe dans columns - utiliser le composant Header complet
    const col = columnData || columns.find(c => c.key === key);
    const colIdx = leafIndex !== undefined ? leafIndex : columns.findIndex((c) => c.key === key);
    const nextColKey = colIdx !== columns.length - 1 ? columns[colIdx + 1]?.key : undefined;
    const finalIsLastColumn = isLastColumn !== undefined ? isLastColumn : (colIdx === columns.length - 1);

    return (
      <Header
        rowSpan={rowSpan}
        colSpan={colSpan}
        isLastColumn={finalIsLastColumn}
        isFirstColumn={colIdx === 0}
        rotation={rotation}
        computeMaxColumnWidth={computeMaxColumnWidth}
        setColumnSizeVars={setColumnSizeVars}
        nextColKey={nextColKey}
        key={key}
        tableIsSelected={isSelected}
        title={title}
        boxId={boxId}
        box={box}
        handleClickInTable={handleClickInTable}
        updateTheadHeight={updateTheadHeight}
        columnKey={key}
        tableStyle={tableStyle}
        align={convertAlign(col?.headerTextAlign ?? col?.textAlign ?? 'start')}
        verticalAlign={convertVerticalAlign(col?.verticalAlign ?? 'center')}
        drawMode={drawMode}
        controlRowsClicked={controlRowsClicked}
        setControlRowsClicked={setControlRowsClicked}
        zIndex={columns.length - colIdx + 101}
        columnLength={columns.length}
        placementIndex={colIdx + 1}
        isParent={isParent}
        firstRowRef={firstRowRef}
        onControlRowClick={onControlRowClick}
        columnWidthPercent={columnWidthPercentMap[key]}
        // Seul un en-tête qui descend jusqu'à la dernière ligne d'en-têtes
        // touche le corps du tableau.
        bodyEdgeStyle={rowIndex + (rowSpan ?? 1) === headerRows.length ? bodyEdgeStyle : undefined}
      />
    )
  }

  // Fallback: utiliser la structure simple si pas de columnsDisposition
  const renderSimpleHeader = (header, rowSpan = 1) => {
    const colIdx = columns.findIndex((col) => col.key === header.id)
    const col = columns[colIdx]
    const nextColKey = colIdx !== columns.length - 1 ? columns[colIdx + 1].key : undefined

    return (
      <Header
        rowSpan={rowSpan}
        isLastColumn={colIdx === columns.length - 1}
        isFirstColumn={colIdx === 0}
        rotation={rotation}
        computeMaxColumnWidth={computeMaxColumnWidth}
        setColumnSizeVars={setColumnSizeVars}
        nextColKey={nextColKey}
        key={header.id}
        tableIsSelected={isSelected}
        title={col.title}
        boxId={boxId}
        box={box}
        handleClickInTable={handleClickInTable}
        updateTheadHeight={updateTheadHeight}
        columnKey={col.key}
        tableStyle={tableStyle}
        align={convertAlign(col.headerTextAlign ?? col.textAlign ?? 'start')}
        verticalAlign={convertVerticalAlign(col.verticalAlign ?? 'center')}
        drawMode={drawMode}
        controlRowsClicked={controlRowsClicked}
        setControlRowsClicked={setControlRowsClicked}
        zIndex={columns.length - colIdx + 101}
        columnLength={columns.length}
        placementIndex={colIdx + 1}
        onControlRowClick={onControlRowClick}
        columnWidthPercent={columnWidthPercentMap[col?.key]}
        bodyEdgeStyle={bodyEdgeStyle}
      />
    )
  }

  return (
    <>
      <colgroup>
        {columns.map((col) => (
          <col key={col.key} style={{ width: `calc(var(--${col.key}-size) * 1px - 1px)` }} />
        ))}
      </colgroup>
      <thead>
        {hasDisposition ? (
          // Rendu avec columnsDisposition
          headerRows.map((row, rowIdx) => (
            <tr key={`header-row-${rowIdx}`} ref={rowIdx === 0 ? firstRowRef : null}>
              {row.map((headerData, colIdx) => renderHeader(headerData, colIdx, rowIdx))}
            </tr>
          ))
        ) : (
          // Rendu simple (fallback)
          table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => renderSimpleHeader(header, 1))}
            </tr>
          ))
        )}
      </thead>
    </>
  )
}

export default TableHead
